import {
  CHANNEL_KINDS,
  CONVERSATION_STATUSES,
  META_SESSION_TYPES,
  OPERATION_MODE_KINDS,
  type MetaSessionType,
} from '@lume/shared';
import { ChevronDown } from 'lucide-react';
import { useState } from 'react';

import { CapabilityGate } from '../../components/CapabilityGate';
import { channelLabel, modeLabel, statusLabel } from '../conversations/format';

import type { InboxFilters } from './types';

/**
 * Filter rail da Inbox completa (Brief 2B T1).
 *
 * Grupos:
 *   - Status & atribuição (todos os 7 canônicos ADR-054 + assignment chips)
 *   - Estado de espera (awaiting-party) — chip "Todas" limpa o filtro
 *   - Intent (Team-Pro+) via CapabilityGate
 *   - Compromissos (Team-Pro+)
 *   - Bond e relacionamento (Team-Pro+) com sequence + group sub-filtros
 *   - Operação (modo H_a/H_b/H_c)
 *   - Meta (Team-Pro+) — metaSessionType (real, chega ao backend via B7)
 *   - Canal & tempo
 *   - Vistas salvas (presets reais client-side)
 *
 * Capabilities canônicas do registry (12/Jun/2026 — corrige gates que usavam
 * as chaves `inbox.filter.*` do fixtures.js do protótipo, inexistentes no
 * registry → grupos sumiam/travavam mesmo em Team-Professional):
 *   commitments — Solo+ (grupo Compromissos)
 *   conversation-sequences — Team+ (grupo Sequências)
 *   multi-channel-extended — Team+ (grupo Tipo de sessão Meta)
 * Princípio 1 (ADR-126): capability ausente → grupo SOME (sem banner de lock).
 *
 * Honest-defer (omitidos — não há param de filtro no backend):
 *   - Grupo "Intenção detectada": intent não tem modelo/param no backend
 *     (Item NEW-C2) — grupo omitido em vez de checkbox que não filtra.
 *   - Compromissos "Pendentes"/"Sem compromissos": só hasOverdueCommitment
 *     existe como param — única opção real é "Compromisso vencido".
 *   - Flags específicas de sequence (highVolume/escalating/abandonadas): o
 *     param real é hasActiveSequence booleano — uma opção única honesta.
 *   - "Em ConversationGroup": sem param no list-conversations.
 *   - "De equipe" (3º chip de atribuição): sem param de team.
 *   - "Por contrato (autocomplete)": sem busca por contrato no list-conversations.
 *   - "Origem: CTWA / Channel": origem não é param de filtro (ctwa existe como
 *     metaSessionType, coberto pelo grupo Meta; "Channel" não tem equivalente).
 *   - "Pendências hoje" (vista salva): a tela de Pendências é outra (Brief 3 / B1).
 *   - "+ Nova vista": CRUD de SavedInboxView não está no api-client — não finge
 *     persistência; botão omitido.
 *   - Vista "P1 da equipe" (compartilhada): era estática/fake (sem onClick,
 *     pessoa fictícia) — omitida até inbox-shared-views ter CRUD real.
 *   - Nota de auditoria por filtragem (Regulated): removida — o backend não
 *     audita filtragens; exibir a promessa seria claim falso.
 */

export interface InboxFiltersRailProps {
  filters: InboxFilters;
  onSetFilter: <K extends keyof InboxFilters>(key: K, value: InboxFilters[K] | undefined) => void;
  onClear: () => void;
  /**
   * Contagem por status derivada do resultset sem override de aba (mesma
   * fonte dos badges das abas) — design Brief 2B mostra o nº à direita de
   * cada status. Opcional: ausente → linhas sem contagem.
   */
  statusCounts?: Record<string, number> | undefined;
}

export function InboxFiltersRail({
  filters,
  onSetFilter,
  // O design da rail não tem "Limpar" próprio — a limpeza global vive na toolbar.
  // Prop mantida no contrato (InboxPage passa clearFilters); não consumida aqui.
  onClear: _onClear,
  statusCounts,
}: InboxFiltersRailProps): JSX.Element {
  const [openGroups, setOpenGroups] = useState<Record<string, boolean | undefined>>({
    status: true,
    awaiting: true,
    commitment: false,
    seq: false,
    op: false,
    meta: false,
    channel: false,
  });

  const toggleGroup = (id: string): void => setOpenGroups((g) => ({ ...g, [id]: !g[id] }));

  return (
    <aside className="filter-rail" aria-label="Painel de filtros">
      {/* Status & atribuição */}
      <FilterGroup
        id="status"
        title="Status & atribuição"
        open={openGroups.status}
        onToggle={() => toggleGroup('status')}
      >
        {CONVERSATION_STATUSES.map((s) => (
          <FilterRow
            key={s}
            label={statusLabel(s)}
            checked={filters.status === s}
            onToggle={() => onSetFilter('status', filters.status === s ? undefined : s)}
            {...(statusCounts !== undefined && { count: statusCounts[s] ?? 0 })}
          />
        ))}
        <div className="pt-2 flex flex-wrap gap-1">
          <FilterChip
            label="Minhas"
            active={filters.assignedToUserId === 'me'}
            onClick={() =>
              onSetFilter('assignedToUserId', filters.assignedToUserId === 'me' ? undefined : 'me')
            }
          />
          <FilterChip
            label="Não atribuídas"
            active={filters.assignedToUserId === 'unassigned'}
            onClick={() =>
              onSetFilter(
                'assignedToUserId',
                filters.assignedToUserId === 'unassigned' ? undefined : 'unassigned',
              )
            }
          />
          {/* "De equipe": honest-defer — backend não tem param de team. */}
        </div>
      </FilterGroup>

      {/* Estado de espera */}
      <FilterGroup
        id="awaiting"
        title="Estado de espera"
        open={openGroups.awaiting}
        onToggle={() => toggleGroup('awaiting')}
      >
        <div className="flex flex-wrap gap-1">
          <FilterChip
            label="Todas"
            active={filters.awaitingParty === undefined}
            onClick={() => onSetFilter('awaitingParty', undefined)}
          />
          {AWAITING_OPTIONS.map(({ value, label }) => (
            <FilterChip
              key={value}
              label={label}
              active={filters.awaitingParty === value}
              onClick={() =>
                onSetFilter('awaitingParty', filters.awaitingParty === value ? undefined : value)
              }
            />
          ))}
        </div>
      </FilterGroup>

      {/* Grupo "Intenção detectada": honest-defer (Item NEW-C2) — sem modelo
          nem param de intent no backend; omitido em vez de checkbox inerte. */}

      {/* Compromissos — capability canônica `commitments` (Solo+).
          Só "vencido" existe como param (hasOverdueCommitment). */}
      <CapabilityGate capability="commitments">
        <FilterGroup
          id="commitment"
          title="Compromissos"
          open={openGroups.commitment}
          onToggle={() => toggleGroup('commitment')}
        >
          <div className="flex flex-wrap gap-1">
            <FilterChip
              label="Compromisso vencido"
              active={filters.commitmentFilter === 'broken'}
              onClick={() =>
                onSetFilter(
                  'commitmentFilter',
                  filters.commitmentFilter === 'broken' ? undefined : 'broken',
                )
              }
            />
          </div>
        </FilterGroup>
      </CapabilityGate>

      {/* Sequências — capability canônica `conversation-sequences` (Team+).
          O param real é hasActiveSequence (booleano) — opção única honesta. */}
      <CapabilityGate capability="conversation-sequences">
        <FilterGroup
          id="seq"
          title="Sequências"
          open={openGroups.seq}
          onToggle={() => toggleGroup('seq')}
        >
          <FilterRow
            label="Com sequência ativa"
            checked={filters.sequenceFlag !== undefined}
            onToggle={() =>
              onSetFilter('sequenceFlag', filters.sequenceFlag !== undefined ? undefined : 'active')
            }
          />
        </FilterGroup>
      </CapabilityGate>

      {/* Operação */}
      <FilterGroup id="op" title="Operação" open={openGroups.op} onToggle={() => toggleGroup('op')}>
        {OPERATION_MODE_KINDS.map((m) => (
          <FilterRow
            key={m}
            label={modeLabel(m)}
            checked={filters.operationMode === m}
            onToggle={() =>
              onSetFilter('operationMode', filters.operationMode === m ? undefined : m)
            }
          />
        ))}
        {/* "Origem: CTWA / Channel": honest-defer — origem não é param de filtro
            (ctwa existe como metaSessionType no grupo Meta abaixo). */}
      </FilterGroup>

      {/* Meta — capability canônica `multi-channel-extended` (Team+); o filtro
          é real (chega ao backend via metaSessionType, B7). */}
      <CapabilityGate capability="multi-channel-extended">
        <FilterGroup
          id="meta"
          title="Tipo de sessão Meta"
          open={openGroups.meta}
          onToggle={() => toggleGroup('meta')}
        >
          {META_SESSION_TYPES.map((t) => (
            <FilterRow
              key={t}
              label={metaSessionLabel(t)}
              checked={filters.metaSessionType === t}
              onToggle={() =>
                onSetFilter('metaSessionType', filters.metaSessionType === t ? undefined : t)
              }
            />
          ))}
        </FilterGroup>
      </CapabilityGate>

      {/* Canal & tempo */}
      <FilterGroup
        id="channel"
        title="Canal"
        open={openGroups.channel}
        onToggle={() => toggleGroup('channel')}
      >
        {CHANNEL_KINDS.map((ch) => (
          <FilterRow
            key={ch}
            label={channelLabel(ch)}
            checked={filters.channel === ch}
            onToggle={() => onSetFilter('channel', filters.channel === ch ? undefined : ch)}
          />
        ))}
      </FilterGroup>

      {/* Vistas salvas — presets reais client-side */}
      <div className="saved-views">
        <p className="filter-group__title pb-2">Vistas salvas</p>
        <SavedViewRow
          label="Minhas urgentes"
          onClick={() => onSetFilter('assignedToUserId', 'me')}
        />
        <SavedViewRow
          label="Aguardando retorno"
          onClick={() => onSetFilter('awaitingParty', 'customer')}
        />
        {/* "Pendências hoje": honest-defer — a tela de Pendências é outra (B1). */}
        {/* "P1 da equipe" (compartilhada): honest-defer — era estática/fake;
            volta quando inbox-shared-views tiver CRUD real. */}
        {/* "+ Nova vista": honest-defer — CRUD de SavedInboxView não está no api-client. */}
      </div>
    </aside>
  );
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function FilterGroup({
  title,
  open: openRaw,
  onToggle,
  children,
}: {
  id: string;
  title: string;
  open: boolean | undefined;
  onToggle: () => void;
  children: React.ReactNode;
}): JSX.Element {
  const open = openRaw ?? false;
  return (
    <div className="filter-group" data-collapsed={!open}>
      <button
        type="button"
        className="filter-group__head w-full"
        onClick={onToggle}
        aria-expanded={open}
      >
        <span className="filter-group__title">{title}</span>
        <ChevronDown className="filter-group__chev" aria-hidden />
      </button>
      <div className="filter-group__body">{children}</div>
    </div>
  );
}

function FilterRow({
  label,
  checked,
  onToggle,
  count,
}: {
  label: string;
  checked: boolean;
  onToggle: () => void;
  count?: number;
}): JSX.Element {
  return (
    <label className="filter-row cursor-pointer">
      <input
        type="checkbox"
        checked={checked}
        onChange={onToggle}
        className="h-3 w-3 rounded border-border-subtle"
      />
      <span className="flex-1">{label}</span>
      {count !== undefined && <span className="filter-row__count">{count}</span>}
    </label>
  );
}

function FilterChip({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className={['filter-chip', active ? 'filter-chip--active' : ''].filter(Boolean).join(' ')}
    >
      {label}
    </button>
  );
}

function SavedViewRow({
  label,
  shared,
  onClick,
}: {
  label: string;
  shared?: string;
  onClick?: () => void;
}): JSX.Element {
  return (
    <button type="button" className="saved-view-row w-full text-left" onClick={onClick}>
      <span className="flex-1">{label}</span>
      {shared && <span className="saved-view-row__shared">{shared}</span>}
    </button>
  );
}

// ─── Static data ─────────────────────────────────────────────────────────────

const AWAITING_OPTIONS = [
  { value: 'customer' as const, label: 'Cliente' },
  { value: 'operator' as const, label: 'Operador' },
  { value: 'system' as const, label: 'Sistema' },
];

const META_SESSION_LABELS: Record<MetaSessionType, string> = {
  customer_care: 'Janela 24h',
  service: 'Atendimento',
  utility: 'Utilitário',
  authentication: 'Autenticação',
  marketing: 'Marketing',
  ctwa: 'Anúncio (CTWA)',
};

function metaSessionLabel(t: MetaSessionType): string {
  return META_SESSION_LABELS[t] ?? t;
}
